import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  heroImageUrl: string;
  heroImageAlt: string;
  heroImageTitle: string;
  breadcrumbLabel: string;
  readingTime: string;
  children: ReactNode;
  relatedLinks: { label: string; to: string }[];
}

export default function ArticleLayout({
  title,
  subtitle,
  heroImageUrl,
  heroImageAlt,
  heroImageTitle,
  breadcrumbLabel,
  readingTime,
  children,
  relatedLinks,
}: ArticleLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="px-6 md:px-10 lg:px-20">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-6">
              <Link
                to="/"
                className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-900">
                  hipobuy<span className="text-primary-500">spread</span>
                </span>
              </Link>
              <span className="hidden md:inline text-sm text-foreground-400">|</span>
              <span className="hidden md:inline text-sm font-medium text-foreground-500">{breadcrumbLabel}</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                ← Back to Hub
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="px-6 md:px-10 lg:px-20 max-w-[1000px] mx-auto py-12 md:py-20">
        <article>
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-foreground-400 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <span>/</span>
              <span>{breadcrumbLabel}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-6">
              {title}
            </h1>
            <p className="text-foreground-500 text-lg leading-relaxed mb-4">
              {subtitle}
            </p>
            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span>Updated: July 2026</span>
              <span>·</span>
              <span>{readingTime} min read</span>
              <span>·</span>
              <span>By Hipobuy Spreadsheet Team</span>
            </div>
          </header>

          <img
            src={heroImageUrl}
            alt={heroImageAlt}
            title={heroImageTitle}
            className="w-full h-auto rounded-2xl object-cover mb-12"
            style={{ maxHeight: '500px' }}
          />

          <div className="prose-custom">
            {children}
          </div>
        </article>

        {relatedLinks.length > 0 && (
          <aside className="mt-16 pt-10 border-t border-background-200/60">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-5">Continue Reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-3 p-4 bg-background-100 rounded-xl hover:bg-accent-50/50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-article-line"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{link.label}</span>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </main>

      <footer className="w-full bg-foreground-900 text-white py-10">
        <div className="px-6 md:px-10 lg:px-20 max-w-[1000px] mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="font-heading text-xl font-bold text-white hover:text-accent-400 transition-colors cursor-pointer">
              hipobuy<span className="text-primary-500">spread</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-foreground-400 text-sm hover:text-white transition-colors cursor-pointer underline">Home</Link>
              <Link to="/kakobuy-spreadsheet" className="text-foreground-400 text-sm hover:text-white transition-colors cursor-pointer underline">Kakobuy</Link>
              <Link to="/cssbuy-spreadsheet" className="text-foreground-400 text-sm hover:text-white transition-colors cursor-pointer underline">CSSBuy</Link>
              <Link to="/superbuy-spreadsheet" className="text-foreground-400 text-sm hover:text-white transition-colors cursor-pointer underline">Superbuy</Link>
              <Link to="/oopbuy-spreadsheet" className="text-foreground-400 text-sm hover:text-white transition-colors cursor-pointer underline">Oopbuy</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-white transition-colors cursor-pointer underline">Kaspreadsheet</a>
            </div>
            <p className="text-foreground-500 text-sm">&copy; 2026 Hipobuy Spreadsheet Hub</p>
          </div>
        </div>
      </footer>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "description": subtitle,
            "datePublished": "2026-07-09",
            "dateModified": "2026-07-09",
            "author": { "@type": "Organization", "name": "Hipobuy Spreadsheet Hub" },
            "publisher": { "@type": "Organization", "name": "Hipobuy Spreadsheet Hub" },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://hipobuyspreads.store" }
          })
        }}
      />
    </div>
  );
}